import { Button } from 'antd';
import dayjs from 'dayjs';
import { BriefcaseBusiness, Check, Users, Wallet } from 'lucide-react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import type { TJobPost } from '../../../app/features/recruitment/recruitment.types';
import LoginModal from '../../../components/LoginModal';
import useAuth from '../../../hooks/useAuth';
import useDiscloser from '../../../hooks/useDiscloser';
import cn from '../../../lib/cn';
import { MetaItem, OrgAvatar, panelClass } from './portal';
import {
  formatExperience,
  formatSalary,
  getDeadline,
  toPreview,
} from './portal.utils';
import { PATHS } from '../../../routes/paths';

type ModalType = 'LOGIN';

export const JobCard = ({ job }: { job: TJobPost }) => {
  const navigate = useNavigate();
  const { verifiedInfo } = useAuth();
  const [config, handleOpen, handleClose] = useDiscloser<ModalType>();

  const hasApplied = job.has_applied || job.applied_status;
  const deadline = getDeadline(job.job_application_deadline);

  const handleApply = (e: MouseEvent) => {
    e.stopPropagation();
    if (!verifiedInfo) {
      handleOpen({ type: 'LOGIN' });
      return;
    }
    navigate(PATHS.jobPortal.apply(job.id));
  };

  return (
    <>
      <article
        onClick={() => navigate(PATHS.jobPortal.job(job.id))}
        className={cn(
          panelClass,
          'group cursor-pointer p-5 transition-colors hover:border-theme-primary/40 sm:p-6',
        )}
      >
        <div className="flex gap-4">
          <OrgAvatar name={job.org_name} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="line-clamp-2 text-base font-semibold text-gray-900 transition-colors group-hover:text-theme-primary sm:truncate">
                  {job.job_title}
                </h3>
                <p className="mt-0.5 truncate text-sm text-gray-500">
                  {job.org_name}
                  {job.department_name && ` · ${job.department_name}`}
                </p>
              </div>

              {hasApplied ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/15">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  Applied
                </span>
              ) : (
                <Button
                  onClick={handleApply}
                  className="btn-primary h-8 shrink-0 rounded-full px-4 text-[13px] font-medium"
                >
                  Apply
                </Button>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
              <MetaItem icon={BriefcaseBusiness}>
                {formatExperience(job.job_experience)}
              </MetaItem>
              <MetaItem icon={Wallet}>
                {formatSalary(
                  job.salary_min_bdt,
                  job.salary_max_bdt,
                  job.is_show_salary_public,
                )}
              </MetaItem>
              <MetaItem icon={Users}>
                {job.vacancy} opening{job.vacancy === 1 ? '' : 's'}
              </MetaItem>
            </div>

            {job.job_description && (
              <p className="mt-3 line-clamp-2 text-sm leading-6 text-gray-500">
                {toPreview(job.job_description)}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4 text-xs">
              <span className="text-gray-400">
                {job.published_date
                  ? `Posted ${dayjs(job.published_date).fromNow()}`
                  : ''}
              </span>
              {deadline && (
                <span
                  className={cn(
                    'font-medium',
                    deadline.urgent ? 'text-amber-600' : 'text-gray-500',
                  )}
                >
                  {deadline.label}
                </span>
              )}
            </div>
          </div>
        </div>
      </article>

      <LoginModal
        data={config.data}
        type={config.type}
        handleClose={handleClose}
        open={config.open && config.type === 'LOGIN'}
      />
    </>
  );
};
